import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, User, FileText, Clock } from "lucide-react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import AttendancePanel from "../../components/Attendance/AttendancePanel";
import TodayAttendanceStatus from "../../components/Attendance/TodayAttendanceStatus";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

export default function ManagerAttendance() {
  const navigate = useNavigate();
  const email = useSelector(getUserEmail);
  const { data: userByEmail } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  // Get academy ID from user data
  const academyId = userByEmail?.user?.academyId;
  const currentYear = new Date().getFullYear();

  // Check if calendar exists for current year
  const {
    data: calendarData,
    isLoading: isLoadingCalendar,
    error: calendarError,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: currentYear,
    },
    {
      skip: !academyId,
    },
  );

  // Check if calendar exists
  const hasCalendar = useMemo(() => {
    if (!academyId) return false;
    if (isLoadingCalendar) return true; // Assume exists while loading to avoid flicker
    if (calendarError) {
      const status = calendarError?.status || calendarError?.originalStatus;
      if (status === 404 || status === "FETCH_ERROR") {
        return false;
      }
      return true;
    }
    return (
      calendarData?.calendar !== null && calendarData?.calendar !== undefined
    );
  }, [calendarData, calendarError, isLoadingCalendar, academyId]);

  const handleClockIn = () => {
    // TODO: Implement clock in functionality with camera and location capture
    console.log("Clock in clicked");
  };

  const handleNavigateToRecords = (path) => {
    navigate(path);
  };

  // If no calendar exists, show message
  if (!hasCalendar && !isLoadingCalendar) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-center p-8">
          <Calendar className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-zinc-800 mb-2">
            No Calendar Created
          </h2>
          <p className="text-zinc-600">
            Please come back when a calendar has been created for your academy.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 h-full">
      <AttendancePanel />

      {/* Other Options Section */}
      <div className="bg-white py-6 px-8 h-full">
        <h2 className="text-2xl font-bold text-zinc-800 mb-2">Other options</h2>
        <p className="text-zinc-600 mb-6">
          A quick link to other options you can do for today
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* My Leave Records Card */}
          <button
            onClick={() =>
              handleNavigateToRecords("/manager/attendance/leave-records")
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#CCB1E3] p-3 rounded-lg">
                <Calendar className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  My Leave Records
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Check how many your leave history and leave balance
                </p>
              </div>
            </div>
          </button>

          {/* My Attendance Records Card */}
          <button
            onClick={() =>
              handleNavigateToRecords("/manager/attendance/record")
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#CCB1E3] p-3 rounded-lg">
                <FileText className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  My Attendance Records
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Work hours. Leave hours and many more.
                </p>
              </div>
            </div>
          </button>

          {/* Apply Leave Card */}
          <button
            onClick={() =>
              handleNavigateToRecords("/manager/attendance/apply-leave")
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#F2B5D4] p-3 rounded-lg">
                <Clock className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Apply Leave
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Feeling ill or have somewhere to be?
                </p>
              </div>
            </div>
          </button>
          {/* Apply Leave Card */}
          <button
            onClick={() =>
              handleNavigateToRecords(
                "/manager/attendance/employee-manual-leave",
              )
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#F2B5D4] p-3 rounded-lg">
                <Clock className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Add Manual Leave
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Add a manual leave for your employees
                </p>
              </div>
            </div>
          </button>

          {/* Employee Leave Requests Card */}
          <button
            onClick={() =>
              handleNavigateToRecords(
                "/manager/attendance/employee-leave-requests",
              )
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#B9E5DD] p-3 rounded-lg">
                <User className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Employee Leave Requests
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Check your subordinates leave request
                </p>
              </div>
            </div>
          </button>

          {/* Employee Attendance Card */}
          <button
            onClick={() =>
              handleNavigateToRecords("/manager/attendance/employee-attendance")
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#B9E5DD] p-3 rounded-lg">
                <User className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Employee Attendance
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Check your employee's attendance
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Today's Attendance Status */}
        <TodayAttendanceStatus />
      </div>
    </div>
  );
}
